(() => {
  const originalImageState = new WeakMap();
  const locale = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];

  const finishStoryCopyByLocale = {
    en: {
    Matte: {
      kicker: 'Why matte works',
      title: 'A softer, low-reflection finish lets the artwork speak first.',
      bodyOne: 'Matte keeps hard highlights quieter, helping fine type, line work, and muted color stay easier to read under changing light.',
      bodyTwo: 'Choose it when you want packaging, event pieces, artwork, or personal projects to feel calm, understated, and considered.',
      cardTitle: 'Less reflection',
      cardCopy: 'Direct light has less visual competition with fine artwork, text, and softer color palettes.',
      featureAlt: 'A matte custom sticker staying readable under angled light',
      performanceAlt: 'Macro view of a matte sticker face, crisp printed edge, and backing layer'
    },
    Glossy: {
      kicker: 'Why glossy works',
      title: 'A bright, polished finish gives color and shine more presence.',
      bodyOne: 'Glossy reflects more light, adding vivid contrast and a smooth, polished surface that can make bold color feel more energetic.',
      bodyTwo: 'Choose it when you want packaging, promotions, events, artwork, or personal projects to feel bright, lively, and high-impact.',
      cardTitle: 'More shine',
      cardCopy: 'Reflected highlights add visual energy to bold artwork, saturated color, and high-contrast designs.',
      featureAlt: 'Bright custom glossy stickers with a smooth polished surface',
      performanceAlt: 'Colorful custom glossy stickers showing a bright polished finish'
    }
    },
    fr: {
      Matte: {
        kicker: 'Pourquoi choisir le mat',
        title: 'Une finition douce et peu réfléchissante laisse le visuel passer en premier.',
        bodyOne: 'Le mat atténue les reflets marqués et aide les petits caractères, les traits fins et les couleurs douces à rester lisibles lorsque la lumière change.',
        bodyTwo: 'Choisissez-le pour donner aux emballages, événements, illustrations et projets personnels un aspect calme, sobre et soigné.',
        cardTitle: 'Moins de reflets',
        cardCopy: 'La lumière directe concurrence moins les détails fins, le texte et les palettes de couleurs douces.',
        featureAlt: 'Autocollant personnalisé mat restant lisible sous une lumière inclinée',
        performanceAlt: 'Gros plan sur la surface mate, le bord imprimé net et le support d’un autocollant'
      },
      Glossy: {
        kicker: 'Pourquoi choisir le brillant',
        title: 'Une finition lumineuse et polie donne plus de présence aux couleurs et à l’éclat.',
        bodyOne: 'Le brillant réfléchit davantage la lumière, renforce le contraste et offre une surface lisse qui dynamise les couleurs franches.',
        bodyTwo: 'Choisissez-le pour des emballages, promotions, événements, illustrations ou projets personnels lumineux et percutants.',
        cardTitle: 'Plus de brillance',
        cardCopy: 'Les reflets donnent de l’énergie aux visuels audacieux, aux couleurs saturées et aux designs très contrastés.',
        featureAlt: 'Autocollants personnalisés brillants avec une surface lisse et polie',
        performanceAlt: 'Autocollants personnalisés colorés montrant une finition brillante'
      }
    },
    de: {
      Matte: {
        kicker: 'Warum matt funktioniert',
        title: 'Eine weiche, reflexionsarme Oberfläche stellt das Motiv in den Mittelpunkt.',
        bodyOne: 'Matt reduziert harte Lichtreflexe. Feine Schrift, Linien und gedeckte Farben bleiben bei wechselndem Licht leichter lesbar.',
        bodyTwo: 'Wählen Sie Matt, wenn Verpackungen, Events, Illustrationen oder persönliche Projekte ruhig, zurückhaltend und durchdacht wirken sollen.',
        cardTitle: 'Weniger Reflexion',
        cardCopy: 'Direktes Licht konkurriert weniger mit feinen Details, Text und sanften Farbpaletten.',
        featureAlt: 'Matter individueller Aufkleber, der bei schrägem Licht gut lesbar bleibt',
        performanceAlt: 'Nahaufnahme einer matten Aufkleberoberfläche mit sauberer Druckkante und Trägerpapier'
      },
      Glossy: {
        kicker: 'Warum glänzend funktioniert',
        title: 'Eine helle, polierte Oberfläche verleiht Farben und Glanz mehr Präsenz.',
        bodyOne: 'Glanz reflektiert mehr Licht, verstärkt Kontraste und lässt kräftige Farben auf einer glatten Oberfläche lebendiger wirken.',
        bodyTwo: 'Wählen Sie Glanz für Verpackungen, Aktionen, Events, Illustrationen oder persönliche Projekte mit heller, lebendiger Wirkung.',
        cardTitle: 'Mehr Glanz',
        cardCopy: 'Reflexe geben kräftigen Motiven, satten Farben und kontrastreichen Designs zusätzliche Energie.',
        featureAlt: 'Helle individuelle Glanzaufkleber mit glatter polierter Oberfläche',
        performanceAlt: 'Farbige individuelle Glanzaufkleber mit heller polierter Oberfläche'
      }
    }
  };
  const finishStoryCopy = finishStoryCopyByLocale[locale] || finishStoryCopyByLocale.en;
  const interfaceCopy = {
    en: { selected: 'Selected finish', other: 'Other finish', mobileAlt: 'The same custom sticker used consistently across packaging and everyday items', reviews: 'Read customer reviews' },
    fr: { selected: 'Finition sélectionnée', other: 'Autre finition', mobileAlt: 'Le même autocollant personnalisé utilisé sur des emballages et des objets du quotidien', reviews: 'Lire les avis clients' },
    de: { selected: 'Gewählte Oberfläche', other: 'Andere Oberfläche', mobileAlt: 'Derselbe individuelle Aufkleber einheitlich auf Verpackungen und Alltagsgegenständen', reviews: 'Kundenbewertungen lesen' }
  }[locale] || { selected: 'Selected finish', other: 'Other finish', mobileAlt: 'The same custom sticker used consistently across packaging and everyday items', reviews: 'Read customer reviews' };

  const rememberImage = (image) => {
    if (!originalImageState.has(image)) {
      originalImageState.set(image, {
        src: image.getAttribute('src'),
        srcset: image.getAttribute('srcset'),
        sizes: image.getAttribute('sizes'),
        dataSrc: image.getAttribute('data-src'),
        alt: image.getAttribute('alt')
      });
    }
    return originalImageState.get(image);
  };

  const useImage = (image, source, alt) => {
    rememberImage(image);
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.removeAttribute('data-src');
    image.src = source;
    image.alt = alt;
  };

  const applyFinishStory = (root, requestedFinish) => {
    const finish = requestedFinish === 'Glossy' || String(requestedFinish).toLowerCase() === 'glossy' ? 'Glossy' : 'Matte';
    const copy = finishStoryCopy[finish];
    root.dataset.selectedFinish = finish.toLowerCase();

    const textMap = {
      '[data-finish-kicker]': copy.kicker,
      '[data-finish-title]': copy.title,
      '[data-finish-body-one]': copy.bodyOne,
      '[data-finish-body-two]': copy.bodyTwo,
      '[data-finish-card-title]': copy.cardTitle,
      '[data-finish-card-copy]': copy.cardCopy
    };
    Object.entries(textMap).forEach(([selector, value]) => {
      const node = root.querySelector(selector);
      if (node) node.textContent = value;
    });

    const featureImage = root.querySelector('[data-finish-feature-image]');
    const performanceImage = root.querySelector('[data-finish-performance-image]');
    if (featureImage) useImage(featureImage, finish === 'Glossy' ? root.dataset.glossFeatureImage : root.dataset.matteFeatureImage, copy.featureAlt);
    if (performanceImage) useImage(performanceImage, finish === 'Glossy' ? root.dataset.glossPerformanceImage : root.dataset.mattePerformanceImage, copy.performanceAlt);

    root.querySelectorAll('[data-finish-card]').forEach((card) => {
      const isCurrent = card.dataset.finishCard === finish;
      card.classList.toggle('is-current', isCurrent);
      const label = card.querySelector('span');
      if (label) label.textContent = isCurrent ? interfaceCopy.selected : interfaceCopy.other;
    });

  };

  const uniqueImages = (root) => {
    const seen = new Set();
    return [...root.querySelectorAll('.swiper-product img, [id^="product-media-"] img')].filter((image) => {
      const source = image.currentSrc || image.src || image.dataset.src;
      if (!source || seen.has(source)) return false;
      seen.add(source);
      return true;
    });
  };

  const fullResolutionSource = (image) => {
    const source = image.currentSrc || image.src || image.dataset.src;
    if (!source) return '';

    try {
      const url = new URL(source, window.location.href);
      url.searchParams.set('width', '1600');
      return url.toString();
    } catch (error) {
      return source.replace(/([?&])width=\d+/, '$1width=1600');
    }
  };

  const enhanceMobileGallery = (root) => {
    if (!window.matchMedia('(max-width: 960px)').matches) return;

    const slides = document.querySelectorAll('#MainContent.theme-template-suffix-landing-matte-jolie .swiper-product .splide__slide');
    const textHeavySlide = slides[4];
    const image = textHeavySlide?.querySelector('img');
    const cleanImageUrl = root.dataset.mobileSignatureImage;
    if (!image || !cleanImageUrl || textHeavySlide.dataset.inkJolieMobileImage === 'true') return;

    textHeavySlide.dataset.inkJolieMobileImage = 'true';
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.removeAttribute('data-src');
    image.src = cleanImageUrl;
    image.alt = interfaceCopy.mobileAlt;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.width = 1122;
    image.height = 1402;
    image.classList.add('ink-jolie-mobile-clean-image');
  };

  const enhanceHero = (root) => {
    const main = document.querySelector('#MainContent.theme-template-suffix-landing-matte-jolie .section-product-info');
    if (!main || main.dataset.inkJolieHero === 'true') return;
    main.dataset.inkJolieHero = 'true';
    main.classList.add('ink-jolie-product-hero');

    const info = main.querySelector('.product-info') || main.querySelector('.product-content-container');
    if (info && !document.querySelector('#ink-order-panel')) info.id = 'ink-order-panel';

    const media = main.querySelector('.product-media-container');
    if (!media) return;
    const stack = document.createElement('div');
    stack.className = 'ink-jolie-hero-stack';

    const sources = uniqueImages(media).slice(1, 7);

    sources.forEach((source) => {
      const figure = document.createElement('figure');
      const image = source.cloneNode(true);
      image.removeAttribute('id');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.removeAttribute('data-src');
      image.src = fullResolutionSource(source);
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 1254;
      image.height = 1254;
      figure.appendChild(image);
      stack.appendChild(figure);
    });
    media.appendChild(stack);
  };

  const initSticky = (root) => {
    const sticky = root.querySelector('[data-ink-jolie-sticky]');
    const hero = document.querySelector('#MainContent.theme-template-suffix-landing-matte-jolie .section-product-info');
    if (!sticky || !hero) return;
    const observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }, { threshold: 0 });
    observer.observe(hero);
  };

  const scrollToTarget = (target) => {
    if (!target) return;
    const header = document.querySelector('header, .section-header');
    const tabs = document.querySelector('.ink-jolie-matte__tabs');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const tabsHeight = tabs ? tabs.getBoundingClientRect().height : 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - headerHeight - tabsHeight - 16;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  };

  const bindOrderLinks = () => {
    document.querySelectorAll('a[href="#ink-order-panel"]').forEach((link) => {
      if (link.dataset.inkJolieOrderLink === 'true') return;
      link.dataset.inkJolieOrderLink = 'true';
      link.addEventListener('click', (event) => {
        const target = document.querySelector('#ink-order-panel');
        if (!target) return;
        event.preventDefault();
        scrollToTarget(target);
      });
    });
  };

  const enhanceReviewTrigger = () => {
    const trigger = document.querySelector('#MainContent.theme-template-suffix-landing-matte-jolie .section-product-info .x-review-product.judge');
    if (!trigger || trigger.dataset.inkJolieReviewTrigger === 'true') return;

    const count = trigger.textContent.match(/\d+/)?.[0];
    trigger.dataset.inkJolieReviewTrigger = 'true';
    trigger.classList.add('ink-jolie-review-trigger');
    trigger.setAttribute('role', 'link');
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('aria-label', count ? `${interfaceCopy.reviews}: ${count}` : interfaceCopy.reviews);

    const openReviews = (event) => {
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      const target = document.querySelector('#judgeme_product_reviews') || document.querySelector('#ink-jolie-reviews');
      scrollToTarget(target);
    };

    trigger.addEventListener('click', openReviews);
    trigger.addEventListener('keydown', openReviews);
  };

  const enhanceAssuranceIcons = () => {
    const confidence = document.querySelector('#MainContent.theme-template-suffix-landing-matte-jolie .section-product-info .ip-checkout-confidence');
    if (!confidence || confidence.dataset.inkJolieIcons === 'true') return;
    confidence.dataset.inkJolieIcons = 'true';

    const icons = [
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3.5h7l3 3V20.5H7z"/><path d="M14 3.5v4h4M9.5 13l1.6 1.7 3.7-4"/></svg>',
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 6.5h11v10h-11zM14.5 10h3l3 3v3.5h-6z"/><circle cx="7" cy="18" r="1.8"/><circle cx="17.5" cy="18" r="1.8"/></svg>',
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5l7 2.7v5.5c0 4.4-2.8 7.4-7 8.8-4.2-1.4-7-4.4-7-8.8V6.2z"/><path d="M8.8 12.1l2 2 4.4-4.5"/></svg>'
    ];

    confidence.querySelectorAll('li').forEach((item, index) => {
      const icon = item.querySelector('span');
      if (!icon || !icons[index]) return;
      icon.className = 'ink-jolie-assurance-icon';
      icon.innerHTML = icons[index];
    });
  };

  const syncPreviewBarOffset = () => {
    const previewBar = document.querySelector('#PBarNextFrameWrapper');
    const previewOffset = previewBar ? Math.round(previewBar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--ink-jolie-preview-offset', `${previewOffset}px`);
    if (previewBar) previewBar.style.overflow = 'hidden';
  };

  const boot = () => {
    document.querySelectorAll('[data-ink-jolie-matte]').forEach((root) => {
      if (root.dataset.inkJolieReady === 'true') return;
      root.dataset.inkJolieReady = 'true';
      enhanceMobileGallery(root);
      enhanceHero(root);
      initSticky(root);
      const productMain = document.querySelector('#MainContent.theme-template-suffix-landing-matte-jolie');
      const initialFinish = productMain?.dataset.selectedFinish === 'glossy' ? 'Glossy' : 'Matte';
      applyFinishStory(root, initialFinish);
      document.addEventListener('inkelligent:finish-change', (event) => {
        applyFinishStory(root, event.detail?.finish || 'Matte');
      });
    });
    bindOrderLinks();
    enhanceReviewTrigger();
    enhanceAssuranceIcons();
    syncPreviewBarOffset();
    window.setTimeout(syncPreviewBarOffset, 400);
    window.setTimeout(syncPreviewBarOffset, 1200);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
